import React, { useEffect, useMemo, useState } from "react";
import { Copy, Loader2, X } from "lucide-react";
import { formatLocalDate } from "../../../../../utils/formatLocalDate";
import {
  displayDate,
  durationOverlapsRange,
  formatScheduleDate,
} from "../../../core/utils/assignedPlansUtils";

/**
 * Prefill name + dates before navigating into the create/copy plan flow.
 * Blocks continue when dates are invalid, overlap an existing plan, or the
 * name already exists — same rules as create/edit.
 */
export default function CopyDietPlanModal({
  isOpen,
  onClose,
  onContinue,
  planTitle = "",
  startDate: initialStart = "",
  endDate: initialEnd = "",
  existingPlans = [],
  continuing = false,
}) {
  const [planName, setPlanName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const today = formatLocalDate(new Date());
    const week = new Date();
    week.setDate(week.getDate() + 7);
    setPlanName(planTitle ? `Copy · ${planTitle}` : "Copy plan");
    // Prefer a fresh window (today → +7). Prefilling the source plan's dates
    // almost always overlaps that same plan and blocks create immediately.
    setStartDate(initialStart || today);
    setEndDate(initialEnd || formatLocalDate(week));
  }, [isOpen, planTitle, initialStart, initialEnd]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !continuing) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, continuing]);

  const plansForChecks = useMemo(
    () =>
      (existingPlans || []).map((p) => ({
        ...p,
        start_date: displayDate(p.start_date),
        end_date: displayDate(p.end_date),
      })),
    [existingPlans]
  );

  const dateOrderInvalid = Boolean(startDate && endDate && startDate > endDate);

  const overlappingPlans = useMemo(() => {
    if (!startDate || !endDate || dateOrderInvalid) return [];
    return plansForChecks.filter((p) =>
      durationOverlapsRange(p, startDate, endDate)
    );
  }, [plansForChecks, startDate, endDate, dateOrderInvalid]);

  const duplicateName = useMemo(() => {
    const name = planName.trim().toLowerCase();
    if (!name) return false;
    return plansForChecks.some(
      (p) => (p.title || "").toLowerCase().trim() === name
    );
  }, [plansForChecks, planName]);

  if (!isOpen) return null;

  const canContinue =
    planName.trim().length > 0 &&
    startDate &&
    endDate &&
    !dateOrderInvalid &&
    overlappingPlans.length === 0 &&
    !duplicateName &&
    !continuing;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => !continuing && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="copy-diet-plan-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 bg-orange-100 rounded-full shrink-0">
              <Copy className="w-5 h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <h3 id="copy-diet-plan-title" className="text-lg font-bold text-gray-900">
                Copy plan
              </h3>
              {planTitle ? (
                <p className="text-sm text-gray-500 mt-0.5 truncate" title={planTitle}>
                  From: {planTitle}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={continuing}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {plansForChecks.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                Existing plans (pick dates that don’t overlap)
              </p>
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                {plansForChecks.map((plan) => (
                  <div
                    key={`${plan.id}-${plan.duration_id || plan.start_date || "none"}`}
                    className="inline-flex flex-col px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-left min-w-[140px]"
                  >
                    <span
                      className="font-medium text-gray-800 text-sm truncate"
                      title={plan.title}
                    >
                      {plan.title || "Unnamed plan"}
                    </span>
                    <span className="text-xs text-gray-600 mt-0.5">
                      {plan.start_date
                        ? formatScheduleDate(plan.start_date)
                        : "No dates"}
                      {plan.start_date &&
                        plan.end_date &&
                        plan.start_date !== plan.end_date && (
                          <> – {formatScheduleDate(plan.end_date)}</>
                        )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="copy-plan-name" className="block text-sm font-medium text-gray-700 mb-1">
              New plan name
            </label>
            <input
              id="copy-plan-name"
              type="text"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              disabled={continuing}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none disabled:opacity-70"
            />
            {duplicateName && (
              <p className="text-xs text-amber-700 mt-1">
                A plan with this name already exists. Choose a different name.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="copy-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Start date
              </label>
              <input
                id="copy-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={continuing}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none disabled:opacity-70"
              />
            </div>
            <div>
              <label htmlFor="copy-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                End date
              </label>
              <input
                id="copy-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={continuing}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-primary focus:outline-none disabled:opacity-70"
              />
            </div>
          </div>
          {dateOrderInvalid && (
            <p className="text-xs text-amber-700">Start date must be on or before end date.</p>
          )}
          {!dateOrderInvalid && overlappingPlans.length > 0 && (
            <p className="text-xs text-amber-700">
              These dates overlap{" "}
              {overlappingPlans
                .slice(0, 3)
                .map((p) => p.title || "another plan")
                .join(", ")}
              {overlappingPlans.length > 3 ? "…" : ""}. Pick a free range.
            </p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={continuing}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onContinue({
                planName: planName.trim(),
                startDate,
                endDate,
              })
            }
            disabled={!canContinue}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-orange-primary hover:bg-orange-600 rounded-lg disabled:opacity-50"
          >
            {continuing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
